import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, BarChart3, Scan, ArrowRight, Zap, Target, BookOpen } from 'lucide-react';

const EnglishDiagnostics: React.FC = () => {
    const navigate = useNavigate();

    const handleStartDiagnostic = () => {
        navigate('/eligibility?service=english-prep');
    };

    return (
        <div className="bg-white min-h-screen pt-32 pb-20 font-sans">
            {/* Hero Section */}
            <div className="bg-gradient-to-r from-[#011E26] to-[#009B8F] text-white py-24 mb-16 relative overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <img
                        src="https://images.unsplash.com/photo-1454165205744-3b78555e5572?q=80&w=2070&auto=format&fit=crop"
                        alt="Data Analysis"
                        className="w-full h-full object-cover"
                    />
                </div>
                <div className="max-w-7xl mx-auto px-6 relative z-10">
                    <div className="inline-block px-4 py-1 bg-white/10 backdrop-blur-md rounded-full text-xs font-bold tracking-widest uppercase mb-6 border border-white/20">
                        Precision Testing
                    </div>
                    <h1 className="text-5xl md:text-7xl font-light mb-6 tracking-tight">
                        AI English <span className="font-bold text-[#009B8F]">Diagnostics</span>
                    </h1>
                    <p className="text-xl md:text-2xl text-white/80 max-w-3xl font-light leading-relaxed">
                        Most students don't struggle because they lack ability, they struggle because of confusion.
                        NexOverseas removes this confusion by giving you a fast, intelligent diagnostic.
                    </p>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6">
                {/* Main Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-24">
                    <div>
                        <h2 className="text-3xl md:text-4xl font-bold text-[#011E26] mb-8 leading-tight">
                            From Chaos to <span className="text-[#009B8F]">Clarity</span>.
                        </h2>
                        <div className="prose prose-lg text-slate-600 max-w-none space-y-6">
                            <p>
                                Overwhelmed by choosing between IELTS, PTE, or TOEFL? Unsure where to begin?
                                NexOverseas builds your roadmap from day one. Our data-driven diagnostic reveals
                                your true proficiency level and identifies which global exam aligns best with your strengths.
                            </p>
                            <div className="bg-slate-50 p-8 rounded-2xl border-l-8 border-[#009B8F] shadow-inner">
                                <h3 className="text-xl font-bold text-[#011E26] mb-4">One Portal. One Path. Total Clarity.</h3>
                                <p className="text-slate-600 italic">
                                    "You begin with clarity, not chaos — and finally know exactly where to start."
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="relative">
                        <div className="absolute -inset-4 bg-[#009B8F]/20 rounded-3xl blur-2xl"></div>
                        <img
                            src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=2070&auto=format&fit=crop"
                            alt="Analytics Dashboard"
                            className="relative rounded-3xl shadow-2xl border border-slate-100"
                        />
                    </div>
                </div>

                {/* Feature Cards */}
                <div className="mb-24">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold text-[#011E26] mb-4">Your Personalised Proficiency Report</h2>
                        <p className="text-slate-500 text-lg">Stop Guessing. Start Improving.</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            {
                                icon: <Target className="w-10 h-10 text-[#009B8F]" />,
                                title: "Estimated Score Ranges",
                                desc: "See your predicted performance across major global exams (IELTS, PTE, TOEFL)."
                            },
                            {
                                icon: <BarChart3 className="w-10 h-10 text-[#009B8F]" />,
                                title: "Skill Gap Analysis",
                                desc: "A clear breakdown of your strongest and weakest skills so you know exactly what to focus on next."
                            },
                            {
                                icon: <Zap className="w-10 h-10 text-[#009B8F]" />,
                                title: "Automated Integration",
                                desc: "Your results automatically trigger the creation of your Personalised Automated Study Path."
                            }
                        ].map((feature, i) => (
                            <div key={i} className="p-10 bg-white border border-slate-100 rounded-3xl shadow-xl hover:shadow-2xl transition-all hover:-translate-y-2 group">
                                <div className="mb-6 p-4 bg-slate-50 rounded-2xl w-fit group-hover:bg-[#009B8F]/10 transition-colors">
                                    {feature.icon}
                                </div>
                                <h3 className="text-2xl font-bold text-[#011E26] mb-4">{feature.title}</h3>
                                <p className="text-slate-600 leading-relaxed">{feature.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* CTA Section */}
                <div className="bg-[#011E26] rounded-[3rem] p-12 md:p-20 text-center relative overflow-hidden shadow-2xl">
                    <div className="absolute top-0 right-0 w-96 h-96 bg-[#009B8F]/20 rounded-full blur-[100px] -mr-48 -mt-48"></div>
                    <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#009B8F]/10 rounded-full blur-[100px] -ml-48 -mb-48"></div>

                    <div className="relative z-10">
                        <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">Start Your Data-Driven Journey</h2>
                        <p className="text-white/70 text-xl mb-12 max-w-2xl mx-auto">
                            Ready to find your path? Let our AI analyze your current level and build your success roadmap today.
                        </p>
                        <button
                            onClick={handleStartDiagnostic}
                            className="group bg-[#009B8F] text-white px-10 py-5 rounded-full font-bold text-lg hover:bg-white hover:text-[#011E26] transition-all flex items-center gap-3 mx-auto shadow-xl"
                        >
                            Start Your Diagnostic Scan
                            <ArrowRight className="group-hover:translate-x-2 transition-transform" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EnglishDiagnostics;
